import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import './TeacherGradingView.css';

const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const asUuid = (v) => (typeof v === 'string' && UUID_RE.test(v) ? v : null);

// iOS decodes dates with ISO8601DateFormatter (.withInternetDateTime) which does
// NOT accept fractional seconds — strip ".SSS" so the string parses on iOS.
const toIsoString = (d) => d.toISOString().replace(/\.\d{3}Z$/, 'Z');

function TeacherGradingView({ teacherData }) {
  const [assignments, setAssignments] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [students, setStudents] = useState([]);
  const [grades, setGrades] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadClasses = useCallback(async () => {
    if (!teacherData?.uid) return;
    try {
      const classesQuery = query(
        collection(db, 'classes'),
        where('teacherId', '==', teacherData.uid)
      );
      const snapshot = await getDocs(classesQuery);
      const classesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setClasses(classesData);
      if (classesData.length > 0) {
        setSelectedClassId(classesData[0].id);
      }
    } catch (error) {
      console.error('Error loading classes:', error);
    }
  }, [teacherData]);

  const loadAssignments = useCallback(async () => {
    if (!teacherData?.uid) {
      setLoading(false);
      return;
    }
    try {
      const assignmentsData = [];
      for (const cls of classes) {
        const assignmentsQuery = query(
          collection(db, 'classes', cls.id, 'assignments')
        );
        const snapshot = await getDocs(assignmentsQuery);
        const classAssignments = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        assignmentsData.push(...classAssignments);
      }
      setAssignments(assignmentsData);
    } catch (error) {
      console.error('Error loading assignments:', error);
    } finally {
      setLoading(false);
    }
  }, [classes, teacherData]);

  const loadStudents = useCallback(async () => {
    try {
      const studentsQuery = query(
        collection(db, 'classes', selectedClassId, 'students'),
        where('isActive', '==', true)
      );
      const snapshot = await getDocs(studentsQuery);
      const studentsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setStudents(studentsData);
    } catch (error) {
      console.error('Error loading students:', error);
    }
  }, [selectedClassId]);

  const loadGrades = useCallback(async () => {
    try {
      const gradesData = {};
      const classId = selectedAssignment.classId || selectedClassId;
      // Enumerate the grades subcollection (iOS layout) and match by the
      // `studentId` field — doc keys are UUIDs on iOS, not `studentId_assignmentId`.
      // `__docId` tracks which doc to update when saving edits.
      const gradesSnap = await getDocs(
        collection(db, 'classes', classId, 'assignments', selectedAssignment.id, 'grades')
      );
      const byStudentId = {};
      gradesSnap.docs.forEach(d => {
        const data = d.data();
        const sid = data.studentId;
        if (sid && !byStudentId[sid]) {
          byStudentId[sid] = { docId: d.id, data };
        }
      });
      for (const student of students) {
        const match = byStudentId[student.id]
          || (student.studentId ? byStudentId[student.studentId] : null)
          || (student.uuid ? byStudentId[student.uuid] : null);
        if (match) {
          gradesData[student.id] = { ...match.data, __docId: match.docId };
        }
      }
      setGrades(gradesData);
    } catch (error) {
      console.error('Error loading grades:', error);
    }
  }, [selectedAssignment, students, selectedClassId]);

  useEffect(() => {
    loadClasses();
  }, [loadClasses]);

  useEffect(() => {
    if (classes.length > 0) {
      loadAssignments();
    }
  }, [classes, loadAssignments]);

  useEffect(() => {
    if (selectedClassId) {
      loadStudents();
    }
  }, [selectedClassId, loadStudents]);

  useEffect(() => {
    if (selectedAssignment && students.length > 0) {
      loadGrades();
    }
  }, [selectedAssignment, students, loadGrades]);

  const handleGradeChange = (studentId, score) => {
    setGrades(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        score: parseFloat(score) || 0
      }
    }));
  };

  const handleFeedbackChange = (studentId, feedback) => {
    setGrades(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        feedback
      }
    }));
  };

  const saveGrades = async () => {
    setSaving(true);
    try {
      for (const student of students) {
        const gradeData = grades[student.id];
        if (gradeData && gradeData.score !== undefined) {
          const classId = selectedAssignment.classId || selectedClassId;
          // iOS requires `id` as a UUID and keys the doc by it. Reuse the existing
          // doc key when the grade already exists; otherwise mint a new UUID
          // (the old `studentId_assignmentId` composite key can't decode on iOS).
          const gradeDocId = gradeData.__docId || crypto.randomUUID();
          const gradeRef = doc(db, 'classes', classId, 'assignments', selectedAssignment.id, 'grades', gradeDocId);
          await setDoc(gradeRef, {
            id: asUuid(gradeData.id) || asUuid(gradeDocId) || crypto.randomUUID(),
            studentId: student.id,
            assignmentId: selectedAssignment.id,
            classId,
            score: gradeData.score,
            maxScore: selectedAssignment.maxScore,
            feedback: gradeData.feedback || '',
            gradedBy: teacherData?.uuid || teacherData?.uid,
            gradedAt: toIsoString(new Date()),
            schoolId: teacherData?.schoolId || null
          }, { merge: true });
        }
      }
      alert('Grades saved successfully!');
    } catch (error) {
      console.error('Error saving grades:', error);
      alert('Failed to save grades');
    } finally {
      setSaving(false);
    }
  };

  const calculatePercentage = (score, maxScore) => {
    if (!maxScore || maxScore === 0) return 0;
    return (score / maxScore * 100).toFixed(1);
  };

  const getLetterGrade = (percentage) => {
    if (percentage >= 90) return 'A';
    if (percentage >= 80) return 'B';
    if (percentage >= 70) return 'C';
    if (percentage >= 60) return 'D';
    return 'F';
  };

  const getGradeColor = (percentage) => {
    if (percentage >= 90) return '#22c55e';
    if (percentage >= 80) return '#22c55e';
    if (percentage >= 70) return '#f59e0b';
    if (percentage >= 60) return '#eab308';
    return '#ef4444';
  };

  const filteredAssignments = selectedClassId 
    ? assignments.filter(a => a.classId === selectedClassId)
    : assignments;

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="teacher-grading-view">
      <h2>Grading</h2>

      <div className="grading-controls">
        <div className="control-group">
          <label>Select Class:</label>
          <select
            value={selectedClassId || ''}
            onChange={(e) => setSelectedClassId(e.target.value)}
          >
            {classes.map(cls => (
              <option key={cls.id} value={cls.id}>
                {cls.name} ({cls.grade})
              </option>
            ))}
          </select>
        </div>

        <div className="control-group">
          <label>Select Assignment:</label>
          <select
            value={selectedAssignment?.id || ''}
            onChange={(e) => {
              const assignment = assignments.find(a => a.id === e.target.value);
              setSelectedAssignment(assignment || null);
            }}
          >
            <option value="">Select Assignment</option>
            {filteredAssignments.map(assignment => (
              <option key={assignment.id} value={assignment.id}>
                {assignment.title} ({assignment.maxScore} pts)
              </option>
            ))}
          </select>
        </div>
      </div>

      {!selectedAssignment ? (
        <div className="empty-state">
          <div className="empty-icon">✏️</div>
          <h3>Select an Assignment to Grade</h3>
          <p>Choose a class and assignment to begin grading</p>
        </div>
      ) : (
        <>
          <div className="assignment-info">
            <h3>{selectedAssignment.title}</h3>
            <p>Type: {selectedAssignment.assignmentType}</p>
            <p>Max Score: {selectedAssignment.maxScore} points</p>
          </div>

          {students.length === 0 ? (
            <div className="empty-state">
              <p>No students in this class</p>
            </div>
          ) : (
            <>
              <div className="grading-summary">
                <div className="summary-item graded">
                  <span className="count">{Object.keys(grades).length}</span>
                  <span className="label">Graded</span>
                </div>
                <div className="summary-item ungraded">
                  <span className="count">{students.length - Object.keys(grades).length}</span>
                  <span className="label">Ungraded</span>
                </div>
                <div className="summary-item total">
                  <span className="count">{students.length}</span>
                  <span className="label">Total Students</span>
                </div>
              </div>

              <div className="students-list">
                {students.map(student => {
                  const grade = grades[student.id];
                  const percentage = grade ? calculatePercentage(grade.score, selectedAssignment.maxScore) : 0;
                  const letterGrade = getLetterGrade(percentage);
                  const gradeColor = getGradeColor(percentage);

                  return (
                    <div key={student.id} className="student-row">
                      <div className="student-info">
                        {student.photoURL ? (
                          <img src={student.photoURL} alt={student.fullName} className="student-photo" />
                        ) : (
                          <div className="student-photo placeholder">👤</div>
                        )}
                        <div>
                          <div className="student-name">{student.fullName}</div>
                          <div className="student-id">ID: {student.studentId}</div>
                        </div>
                      </div>
                      <div className="grade-inputs">
                        <div className="score-input">
                          <label>Score</label>
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            max={selectedAssignment.maxScore}
                            value={grade?.score || ''}
                            onChange={(e) => handleGradeChange(student.id, e.target.value)}
                            placeholder="0"
                          />
                          <span className="max-score">/ {selectedAssignment.maxScore}</span>
                        </div>
                        {grade && (
                          <div className="grade-display" style={{ color: gradeColor }}>
                            <span className="percentage">{percentage}%</span>
                            <span className="letter-grade">{letterGrade}</span>
                          </div>
                        )}
                      </div>
                      <div className="feedback-input">
                        <label>Feedback</label>
                        <textarea
                          value={grade?.feedback || ''}
                          onChange={(e) => handleFeedbackChange(student.id, e.target.value)}
                          placeholder="Add feedback..."
                          rows={2}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="save-section">
                <button
                  className="save-button"
                  onClick={saveGrades}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save All Grades'}
                </button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default TeacherGradingView;
